import { useState } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { Order, PaymentMethod, StaffMember } from "../../api/types";
import { CodeDialog } from "../../features/codes/CodeDialog";
import { METHODS } from "../../features/orders/order-view";
import { addressOf, contentOf, paymentOf } from "../../features/orders/order-view";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { AppIcon, type AppIconName } from "../AppIcon";

type Filter = "all" | "open" | "paid";

interface Props {
  api: ApiClient;
  staff: StaffMember;
  orders: readonly Order[];
  onTable: (table: string) => void;
  onOrder: (order: Order) => void;
  toast: (message: string, tone?: "info" | "bad") => void;
}

/** "€ 1.003,92" with the cents smaller, as on a bank's card (Furkan's example, 08/10/2026). */
function Amount({ cents, className }: { cents: number; className?: string }) {
  const text = formatEuro(cents);
  const comma = text.lastIndexOf(",");
  return (
    <span className={className}>
      {comma < 0 ? text : text.slice(0, comma)}
      {comma >= 0 && <small>{text.slice(comma)}</small>}
    </span>
  );
}

function iconOf(order: Order): AppIconName {
  if (order.channel === "table") return "table";
  if (order.channel === "delivery") return "courier";
  return "bag";
}

function titleOf(order: Order): string {
  const text = t.tafoApp.home;
  if (order.channel === "table") return t.tafoApp.home.table(order.table ?? "");
  if (order.channel === "delivery") return addressOf(order) || text.delivery;
  const name = order.customer.name ?? "";
  return name === "" ? text.takeaway : text.takeawayFor(name);
}

function stateOf(order: Order): string {
  const text = t.tafoApp.home;
  if (order.status === "cancelled") return text.cancelled;
  if (order.status === "open") return text.open;
  return paymentOf(order);
}

/**
 * Start of the waiter's app (Furkan, 08/10/2026: "een home pagina waar boven zo mooi zijn gegevens staan
 * met mooie icoontjes, midden lijst van bestellingen waar die kan scrollen en eventueel betaalmethode kan
 * wijzigen"): the orders of today with how many there are, and per order its content with "Betaalwijze
 * wijzigen". No card with figures above it any more (Furkan, 19/10/2026): the open tables show on Tafels in
 * the bar at the bottom, the tips do not matter there, his name stands in the red bar. No amount of what he or
 * the shop sold today: nobody sees figures that are not his business (Furkan, 18/10/2026).
 */
export function WaiterHome({ api, staff, orders, onTable, onOrder, toast }: Props) {
  const text = t.tafoApp.home;
  const [filter, setFilter] = useState<Filter>("all");
  const [shown, setShown] = useState<Order | null>(null);
  // the tables and takeaways of today, also a colleague's: a waiter helps at every table and takeaway; the
  // deliveries are the couriers' (Furkan, 18/10/2026)
  const list = orders
    .filter((order) => order.channel === "table" || order.channel === "takeaway")
    .filter((order) =>
      filter === "all" ? true : filter === "open" ? order.status === "open" : order.status === "paid",
    );
  const current = shown === null ? null : (orders.find((order) => order.id === shown.id) ?? shown);

  return (
    <>
      <section className="hm-list" aria-label={text.listLabel}>
        <div className="hm-listhead">
          {/* how many, next to the title: the red card with the figures is gone (Furkan, 19/10/2026) */}
          <h3>
            {text.listTitle} <span className="hm-count">{list.length}</span>
          </h3>
          <div className="hm-filter" role="group" aria-label={text.filterLabel}>
            {(["all", "open", "paid"] as const).map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => {
                  setFilter(key);
                }}
              >
                {text.filters[key]}
              </button>
            ))}
          </div>
        </div>
        {list.length === 0 ? (
          <p className="proto hm-empty">{text.none[filter]}</p>
        ) : (
          <ul className="hm-rows">
            {list.map((order) => (
              <li key={order.id}>
                <button
                  type="button"
                  className={`hm-row${order.status === "cancelled" ? " off" : ""}`}
                  onClick={() => {
                    setShown(order);
                  }}
                >
                  <span className={`hm-icon ${order.channel}`} aria-hidden="true">
                    <AppIcon name={iconOf(order)} size={20} />
                  </span>
                  <span className="hm-what">
                    <b>{titleOf(order)}</b>
                    <small>
                      {order.code} · {formatTime(order.createdAt)} · {stateOf(order)}
                    </small>
                  </span>
                  <Amount cents={order.totalCents} className="hm-amount" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {current && (
        <OrderSheet
          api={api}
          order={current}
          staff={staff}
          onClose={() => {
            setShown(null);
          }}
          onTable={(table) => {
            setShown(null);
            onTable(table);
          }}
          onOrder={onOrder}
          toast={toast}
        />
      )}
    </>
  );
}

/** One order of the list: what is on it, how it was paid, and another payment method afterwards. */
function OrderSheet({
  api,
  order,
  staff,
  onClose,
  onTable,
  onOrder,
  toast,
}: {
  api: ApiClient;
  order: Order;
  staff: StaffMember;
  onClose: () => void;
  onTable: (table: string) => void;
  onOrder: (order: Order) => void;
  toast: (message: string, tone?: "info" | "bad") => void;
}) {
  const text = t.tafoApp.home;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A change that waits for the code of someone who may (a waiter may not, by default). */
  const [asking, setAsking] = useState<{ paymentId: string; from: PaymentMethod; to: PaymentMethod } | null>(null);
  const payments = order.payments.filter((payment) => payment.kind === "payment");
  // also after the Z: the order then counts in the next Z of that day (Furkan, 11/10/2026)
  const changeable = order.status === "paid" && order.source !== "online";

  const change = async (paymentId: string, from: PaymentMethod, to: PaymentMethod, staffToken?: string) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/payments/${paymentId}/method`, {
        body: { method: to },
        ...(staffToken === undefined ? {} : { staffToken }),
      });
      onOrder(updated);
      toast(t.orders.methodChanged(t.paymentMethods[from], t.paymentMethods[to]));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
    } finally {
      setBusy(false);
    }
  };

  const choose = (paymentId: string, from: PaymentMethod, to: PaymentMethod) => {
    if (staff.can.includes("orders.edit")) void change(paymentId, from, to);
    else setAsking({ paymentId, from, to });
  };

  return (
    <>
      <Dialog title={`${titleOf(order)} · ${order.code}`} onClose={onClose}>
        <p className="hm-sheetline">
          <span>
            {formatTime(order.createdAt)}
            {order.createdBy === null ? "" : ` · ${order.createdBy}`}
          </span>
          <b>{formatEuro(order.totalCents)}</b>
        </p>
        <p>{contentOf(order)}</p>
        {order.status === "open" && order.channel === "table" && order.table !== null && (
          <button
            type="button"
            className="btn primary ph-big"
            onClick={() => {
              onTable(order.table!);
            }}
          >
            {text.openTable(order.table)}
          </button>
        )}
        {order.status === "cancelled" && <p className="ph-note bad">{text.cancelled}</p>}
        {changeable && (
          <>
            <h3>{t.orders.methodTitle}</h3>
            {payments.map((payment) => (
              <div className="grp" key={payment.id}>
                {payments.length > 1 && (
                  <p className="proto">
                    {formatTime(payment.createdAt)} · {formatEuro(payment.amountCents)}
                  </p>
                )}
                <div className="chips">
                  {METHODS.map((method) => (
                    <button
                      key={method}
                      type="button"
                      className="chip"
                      aria-pressed={method === payment.method}
                      disabled={busy || method === payment.method}
                      onClick={() => {
                        choose(payment.id, payment.method, method);
                      }}
                    >
                      {t.paymentMethods[method]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <p className="note">{staff.can.includes("orders.edit") ? text.methodNote : text.methodNeedsCode}</p>
          </>
        )}
        {error !== null && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </Dialog>
      {asking && (
        <CodeDialog
          api={api}
          permission="orders.edit"
          title={t.orders.methodTitle}
          note={text.methodNeedsCode}
          onAuthorized={(authorization) => {
            const { paymentId, from, to } = asking;
            setAsking(null);
            void change(paymentId, from, to, authorization.token);
          }}
          onClose={() => {
            setAsking(null);
          }}
        />
      )}
    </>
  );
}
